import type { LocationVarKey } from "@swivl-mono/types";
import { useState } from "react";

import { SearchableLabel } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { CallScriptHistorySection } from "./CallScriptHistory.Section";
import { CallScriptStepsSection } from "./CallScriptSteps.Section";
import { CallScriptsSection } from "./CallScripts.Section";

/**
 * Call Scripts (ENG-3356) — its own Details v3 page, right after Escalation &
 * Ticketing in the rail. Scope comes from the URL like every other Details page.
 */

export const SEARCH_LABELS: SearchableLabel[] = [
  { title: "Call Scripts" },
  { title: "Sales call" },
  { title: "Use this script when" },
  { title: "Steps, in order" },
  { title: "Step library" },
  { title: "Must have" },
  { title: "History" }
];

/** No Cortex variables on this page — call scripts are `call_script` rows. */
export const PAGE_KEYS: LocationVarKey[] = [];

type PageTab = "scripts" | "steps" | "history";

const TAB_LABELS: Record<PageTab, string> = { scripts: "Scripts", steps: "Steps", history: "History" };

export default function CallScriptsPage(): React.ReactElement {
  const orgId = Member.orgId!;
  const page = usePropertiesGetLocationIds();
  const [error, setError] = useState<string | null>(null);
  // The Steps tab is Swivl staff only (ENG-3402, Chuck 2026-09-24): org-wide step library.
  const isStaff = Member.isSwivlStaff;
  const [tab, setTab] = useState<PageTab>("scripts");
  // History (Chuck, 2026-10-01) is for everyone who can see the page: it shows changes and deletes the per-script flyout cannot.
  const tabs: PageTab[] = isStaff ? ["scripts", "steps", "history"] : ["scripts", "history"];

  return (
    // Same container as the other Details v3 pages.
    <div className="mx-auto max-w-[960px] space-y-6 p-6">
      <div className="flex gap-1 border-b border-grey-200">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              tab === t
                ? "-mb-px border-b-2 border-grey-900 px-3 py-2 text-sm font-semibold text-grey-900"
                : "px-3 py-2 text-sm text-grey-500 hover:text-grey-900"
            }
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {tab === "steps" && isStaff ? (
        <CallScriptStepsSection orgId={orgId} onError={setError} />
      ) : tab === "history" ? (
        <CallScriptHistorySection orgId={orgId} onError={setError} />
      ) : (
        <CallScriptsSection orgId={orgId} activeGroupId={page.groupId ?? null} activeLocationId={page.locationId ?? null} onError={setError} />
      )}
    </div>
  );
}
